import { RequireOne } from 'src/types'

export type PageType = RequireOne<{
  path: string
  buildPath?: (args: any) => string
}>

const AuthRoutes = {
  SignIn: {
    path: '/signin',
  },
  SignInPassword: { path: '/signin/password' },
  IdPasswordSignIn: { path: '/signin/id-password' },
  PasswordForget: { path: '/password-forget' },
  PasswordSentEmail: { path: '/password-sent-email' },
} as const

const UpdateInitialProfileRoutes = {
  UpdateInitialPassword: { path: '/update-password' },
  UpdateUserName: { path: '/update-username' },
} as const

const DashboardRoutes = {
  Dashboard: { path: '/dashboard' },
} as const

const ProjectRoutes = {
  ProjectTop: { path: '/projects' },
  ProjectDetail: {
    path: '/projects/[id]',
    buildPath: ({ projectId }: { projectId: string }) => `/projects/${projectId}`,
  },
  ProjectEdit: {
    path: '/projects/[id]/edit',
    buildPath: ({ projectId }: { projectId: string }) => `/projects/${projectId}/edit`,
  },
  ProjectRooms: {
    path: '/projects/[id]/rooms',
    buildPath: ({ projectId }: { projectId: string }) => `/projects/${projectId}/rooms`,
  },
  ProjectRoomsWithGrouping: {
    path: '/projects/[id]/rooms?grouping=true',
    buildPath: ({ projectId }: { projectId: string }) =>
      `/projects/${projectId}/rooms?grouping=true`,
  },
  ProjectRoomsWithApplicationDetail: {
    path: '/projects/[id]/rooms',
    buildPath: ({ projectId, applicationId }: { projectId: string; applicationId: string }) =>
      `/projects/${projectId}/rooms?application_id=${applicationId}`,
  },
  ProjectRoomsWithFilter: {
    path: '/projects/[id]/rooms',
    buildPath: ({ projectId }: { projectId: string }) => `/projects/${projectId}/rooms?filter=true`,
  },
  ProjectRoomsEdit: {
    path: '/projects/[id]/rooms/edit',
    buildPath: ({ projectId }: { projectId: string }) => `/projects/${projectId}/rooms/edit`,
  },
  ProjectSelectMasters: {
    path: '/projects/[id]/masters',
    buildPath: ({ projectId }: { projectId: string }) => `/projects/${projectId}/masters`,
  },
  ProjectSelectMastersEdit: {
    path: '/projects/[id]/masters/edit',
    buildPath: ({ projectId }: { projectId: string }) => `/projects/${projectId}/masters/edit`,
  },
  ProjectSelectMastersEditWithDetail: {
    path: '/projects/[id]/masters/edit',
    buildPath: ({ projectId, selectMasterId }: { projectId: string; selectMasterId: string }) =>
      `/projects/${projectId}/masters/edit?id=${selectMasterId}`,
  },
  ProjectWings: {
    path: '/projects/[id]/wings',
    buildPath: ({ projectId }: { projectId: string }) => `/projects/${projectId}/wings`,
  },
  ProjectWingsEdit: {
    path: '/projects/[id]/wings',
    buildPath: ({ projectId }: { projectId: string }) => `/projects/${projectId}/wings/edit`,
  },
  ProjectCommonContents: { path: '/projects/common-contents' },
  ProjectCommonContentDetail: {
    path: '/projects/common-contents/[id]',
    buildPath: ({ commonContentId }: { commonContentId: number }) =>
      `/projects/common-contents/${commonContentId}`,
  },
  ProjectAnalytics: { path: '/projects/analytics' },
  ProjectDataConnect: { path: '/projects/data-connect' },
}

const UserRoutes = {
  UserList: { path: '/users' },
  ExternalUserList: { path: '/users#external' },
  UserDetail: {
    path: '/users/[userId]',
    buildPath: ({ userId }: { userId: number }) => `/users/${userId}`,
  },
  UserDetailWithProjectDetail: {
    path: '/users/[userId]',
    buildPath: ({ userId, projectId }: { userId: number; projectId: string }) =>
      `/users/${userId}?project=${projectId}`,
  },
  ExternalUserDetail: {
    path: '/external-users/[userId]',
    buildPath: ({ userId }: { userId: number }) => `/external-users/${userId}`,
  },
  ExternalUserDetailWithProjectDetail: {
    path: '/external-users/[userId]',
    buildPath: ({ userId, projectId }: { userId: number; projectId: string }) =>
      `/external-users/${userId}?project=${projectId}`,
  },
}

const SettingRoutes = {
  Setting: { path: '/settings' },
}

const AccountRoutes = {
  Account: { path: '/account' },
  AccountWithProjectDetail: {
    path: '/account',
    buildPath: ({ projectId }: { projectId: string }) => `/account?project=${projectId}`,
  },
}

const HelpRoutes = {
  HelpTop: { path: '/help/top' },
  HelpPolicy: { path: '/help/policy' },
}

export const Routes = {
  Top: { path: '/' },
  ...AuthRoutes,
  ...UpdateInitialProfileRoutes,
  ...DashboardRoutes,
  ...ProjectRoutes,
  ...UserRoutes,
  ...SettingRoutes,
  ...AccountRoutes,
  ...HelpRoutes,
} as const

export type PageList = keyof typeof Routes

// NOTE: path か buildPath 以外が定義されるのを防ぐ
// eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle,@typescript-eslint/no-unused-vars
const _TypedRoutes: { [K in PageList]: PageType } = Routes

export const isProjectPagePath = ({ pathname }: { pathname: string }): boolean => {
  return pathname.startsWith('/projects')
}

export const isRoomPagePath = ({ pathname }: { pathname: string }): boolean => {
  return isProjectPagePath({ pathname }) && pathname.includes('rooms')
}

export const isProjectDetailPagePath = ({
  pathname,
  params,
}: {
  pathname: string
  params: { [key: string]: string | string[] }
}): boolean => {
  const { id } = params
  return pathname.startsWith(`/projects/${id}`)
}
export const isSelectSystemPagePath = ({
  pathname,
  params,
}: {
  pathname: string
  params: { [key: string]: string | string[] }
}): boolean => {
  return (
    isProjectDetailPagePath({ pathname, params }) &&
    (pathname.includes('rooms') || pathname.includes('wings') || pathname.includes('masters'))
  )
}

export const isSelectSystemEditPagePath = ({ pathname }: { pathname: string }): boolean => {
  return (
    (pathname.includes('rooms') && pathname.includes('edit')) ||
    (pathname.includes('wings') && pathname.includes('edit')) ||
    (pathname.includes('masters') && pathname.includes('edit'))
  )
}

export const isUserPagePath = ({ pathname }: { pathname: string }): boolean => {
  const isExternalUserPage = pathname.startsWith('/external-users')
  return Object.values(UserRoutes).some(v => pathname.startsWith(v.path)) || isExternalUserPage
}
export const isSettingPagePath = ({ pathname }: { pathname: string }): boolean => {
  return Object.values(SettingRoutes).some(v => pathname.startsWith(v.path))
}
export const isAccountPagePath = ({ pathname }: { pathname: string }): boolean => {
  return Object.values(AccountRoutes).some(v => pathname.startsWith(v.path))
}

export const isTopPagePath = (pathname: string): boolean => {
  return pathname === Routes.Top.path
}

// 引数のpathnameがユーザの認証に関わるページの場合true
export const isAuthorizationPath = (pathname: string): boolean => {
  return Object.values(AuthRoutes).some(v => v.path === pathname)
}
// 引数のpathnameがログインが必要なページの場合true
export const isRequireLoginPagePath = (pathname: string): boolean => {
  const PrivateRoutes = {
    ...DashboardRoutes,
    ...ProjectRoutes,
    ...UserRoutes,
    ...SettingRoutes,
    ...AccountRoutes,
    ...UpdateInitialProfileRoutes,
    ...HelpRoutes,
  }
  return Object.values(PrivateRoutes).some(v => v.path === pathname)
}
